import { z } from 'zod';
import { richDocSchema } from './richtext.js';
import { GRID_MODES } from './constants.js';
import { boardObjectSchema } from './objects.js';

/** Y.Map `meta` доски. */
export const boardMetaSchema = z.object({
  background: z.string().max(64).default('default'),
  grid: z.enum(GRID_MODES).default('dots'),
  gridSize: z.number().int().min(4).max(200).default(24),
  snapToGrid: z.boolean().default(false),
  /** Версия формата документа — для будущих миграций. */
  schemaVersion: z.number().int().default(1),
});
export type BoardMeta = z.infer<typeof boardMetaSchema>;

/** Пин комментария в Y.Map `comments` (сами треды лежат в app.db). */
export const commentPinSchema = z.object({
  threadId: z.string().max(64),
  x: z.number().finite(),
  y: z.number().finite(),
  /** Если пин привязан к объекту — x/y считаются относительно его левого верхнего угла. */
  objectId: z.string().max(64).nullable().default(null),
  resolved: z.boolean().default(false),
  /** Число комментариев в треде. */
  count: z.number().int().min(0).default(1),
  updatedAt: z.number().int().default(0),
  authorId: z.string().max(64).nullable().default(null),
});
export type CommentPin = z.infer<typeof commentPinSchema>;

/** Сессия голосования (Y.Map `session` → `vote`). */
export const voteSessionSchema = z.object({
  id: z.string(),
  title: z.string().max(200).default('Голосование'),
  votesPerUser: z.number().int().min(1).max(100),
  anonymous: z.boolean(),
  /** null — по всей доске. */
  frameId: z.string().nullable(),
  startedBy: z.string(),
  startedAt: z.number(),
  endsAt: z.number().nullable(),
  status: z.enum(['active', 'finished']),
});
export type VoteSession = z.infer<typeof voteSessionSchema>;

export const timerStateSchema = z.object({
  /** Длительность в мс. */
  duration: z.number().int().min(0),
  /** Время старта (ms epoch) или null, если на паузе. */
  startedAt: z.number().nullable(),
  /** Сколько уже прошло до последней паузы. */
  elapsed: z.number().int().min(0),
  startedBy: z.string().nullable(),
});
export type TimerState = z.infer<typeof timerStateSchema>;

export const presentationStateSchema = z.object({
  presenterId: z.string(),
  presenterName: z.string(),
  frameId: z.string().nullable(),
  startedAt: z.number(),
});
export type PresentationState = z.infer<typeof presentationStateSchema>;

/** Формат экспорта/импорта доски в JSON. */
export const boardExportSchema = z.object({
  // 'holst.board' — файлы, выгруженные до переименования продукта.
  format: z.enum(['lumo.board', 'holst.board']),
  version: z.literal(1),
  name: z.string().max(200).optional(),
  exportedAt: z.string().optional(),
  meta: boardMetaSchema.partial().default({}),
  objects: z.array(boardObjectSchema).max(100_000),
  /** Заметки к доске (rich text). */
  notes: richDocSchema.nullable().optional(),
});
export type BoardExport = z.infer<typeof boardExportSchema>;

/** Awareness-состояние участника. */
export interface PresenceUser {
  id: string;
  name: string;
  color: string;
  avatarUrl?: string | null;
  /** Гость без аккаунта (публичная доска / ссылка). */
  anonymous?: boolean;
}

export interface PresenceState {
  user: PresenceUser;
  cursor: { x: number; y: number } | null;
  selection: string[];
  tool: string;
  viewport: { x: number; y: number; w: number; h: number } | null;
  /** Точки лазерной указки: [x, y, t] */
  laser?: number[] | null;
  /** id участника, за которым следуем. */
  following?: string | null;
  editingId?: string | null;
}
